import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import { queryOptions } from "react-query";

// All profile-field keys share the ["profile-field", ...] prefix, so a single
// invalidateQueries at the root hits every cache entry via prefix matching.
// Root string matches the wire resource name in CircleAi::Intent::RESOURCES.

export const profileFieldKey = createQueryKeyFactory("profile-field");

export interface ProfileFieldPage {
  name: string;
  visible: boolean;
}

export interface ProfileFieldChoice {
  id: number;
  value: string;
}

export interface ProfileField {
  archived?: boolean;
  choices?: ProfileFieldChoice[];
  description: string | null;
  field_type: string;
  id: number;
  key?: string;
  label: string;
  number_options?: unknown;
  pages?: ProfileFieldPage[];
  placeholder?: string | null;
  platform_field?: boolean;
  required?: boolean;
}

export interface PageProfileFieldsResponse {
  hidden_profile_fields: ProfileField[];
  visible_profile_fields: ProfileField[];
}

export const profileFieldQueries = {
  list: () =>
    queryOptions({
      queryKey: profileFieldKey.key("list"),
      queryFn: () =>
        reactQueryGet<ProfileField[]>(internalApi.profileFields.index()),
    }),
  archived: () =>
    queryOptions({
      queryKey: profileFieldKey.key("archived"),
      queryFn: () =>
        reactQueryGet<ProfileField[]>(internalApi.profileFields.archivedList()),
    }),
  detail: (id: number | string) =>
    queryOptions({
      queryKey: profileFieldKey.key("detail", String(id)),
      queryFn: () =>
        reactQueryGet<ProfileField>(internalApi.profileFields.detail(id)),
    }),
  searchable: () =>
    queryOptions({
      queryKey: profileFieldKey.key("searchable"),
      queryFn: () =>
        reactQueryGet<ProfileField[]>(
          internalApi.profileFields.searchableList(),
        ),
    }),
  byKey: (key: string) =>
    queryOptions({
      queryKey: profileFieldKey.key("by-key", key),
      queryFn: () =>
        reactQueryGet<ProfileField>(internalApi.profileFields.showByKey(key)),
    }),
  page: (pageName: string) =>
    queryOptions({
      queryKey: profileFieldKey.key("page", pageName),
      queryFn: () =>
        reactQueryGet<PageProfileFieldsResponse>(
          internalApi.profileFields.pageIndex(pageName),
        ),
    }),
};

export const profileFieldInvalidations = {
  all: () =>
    queryClient.invalidateQueries({ queryKey: profileFieldKey.root() }),
};
